import type { Scenario } from "@/lib/types";

interface SceneRendererProps {
	scenario: Scenario;
	width: number;
	height: number;
}

export function SceneRenderer({ scenario, width, height }: SceneRendererProps) {
	const cx = width / 2;
	const cy = height / 2;

	if (scenario.environment === "ramp") {
		return <RampScene cx={cx} cy={cy} width={width} height={height} angle={scenario.rampAngle ?? 30} shape={scenario.objectShape} />;
	}

	if (scenario.environment === "elevator") {
		return <ElevatorScene cx={cx} cy={cy} width={width} height={height} shape={scenario.objectShape} />;
	}

	if (scenario.environment === "hanging") {
		return <HangingScene cx={cx} cy={cy} width={width} height={height} shape={scenario.objectShape} />;
	}

	if (scenario.environment === "projectile") {
		return <ProjectileScene cx={cx} cy={cy} width={width} height={height} shape={scenario.objectShape} />;
	}

	return <FlatScene cx={cx} cy={cy} width={width} height={height} shape={scenario.objectShape} />;
}

interface SceneProps {
	cx: number;
	cy: number;
	width: number;
	height: number;
	shape: string;
	angle?: number;
}

function ObjectShape({ cx, cy, shape, size = 40 }: { cx: number; cy: number; shape: string; size?: number }) {
	if (shape === "circle") {
		return <circle cx={cx} cy={cy} r={size * 0.6} fill="hsl(220, 70%, 55%)" stroke="hsl(220, 70%, 35%)" strokeWidth={2} />;
	}
	if (shape === "person") {
		return (
			<g>
				{/* Body */}
				<rect x={cx - size * 0.35} y={cy - size * 0.5} width={size * 0.7} height={size * 1.1} rx={4} fill="hsl(220, 70%, 55%)" stroke="hsl(220, 70%, 35%)" strokeWidth={2} />
				{/* Head */}
				<circle cx={cx} cy={cy - size * 0.75} r={size * 0.25} fill="hsl(30, 60%, 70%)" stroke="hsl(30, 40%, 50%)" strokeWidth={2} />
			</g>
		);
	}
	// Box
	return <rect x={cx - size} y={cy - size * 0.7} width={size * 2} height={size * 1.4} rx={4} fill="hsl(220, 70%, 55%)" stroke="hsl(220, 70%, 35%)" strokeWidth={2} />;
}

function FlatScene({ cx, cy, width, height, shape }: SceneProps) {
	const groundY = cy + 40;
	return (
		<g>
			{/* Ground */}
			<line x1={0} y1={groundY} x2={width} y2={groundY} stroke="hsl(var(--muted-foreground))" strokeWidth={2} />
			{/* Hatching below ground */}
			{Array.from({ length: Math.ceil(width / 15) }, (_, i) => (
				<line key={i} x1={i * 15} y1={groundY} x2={i * 15 - 10} y2={groundY + 12} stroke="hsl(var(--muted-foreground))" strokeWidth={1} opacity={0.5} />
			))}
			{/* Object */}
			<ObjectShape cx={cx} cy={cy} shape={shape} />
		</g>
	);
}

function RampScene({ cx, cy, width, height, angle = 30 }: SceneProps & { angle?: number }) {
	const rad = (angle * Math.PI) / 180;
	const rampLen = width * 0.7;
	const rampH = rampLen * Math.sin(rad);
	const rampW = rampLen * Math.cos(rad);

	const baseY = height * 0.75;
	const rampStartX = (width - rampW) / 2;
	const rampEndX = rampStartX + rampW;

	// Object position on the ramp (midpoint)
	const objX = (rampStartX + rampEndX) / 2;
	const objY = baseY - (rampH / 2);

	return (
		<g>
			{/* Ground line */}
			<line x1={0} y1={baseY} x2={width} y2={baseY} stroke="hsl(var(--muted-foreground))" strokeWidth={2} />
			{/* Ramp surface */}
			<polygon
				points={`${rampStartX},${baseY} ${rampEndX},${baseY} ${rampStartX},${baseY - rampH}`}
				fill="hsl(var(--muted))"
				stroke="hsl(var(--muted-foreground))"
				strokeWidth={2}
			/>
			{/* Angle arc */}
			<path
				d={`M ${rampEndX - 40},${baseY} A 40,40 0 0,0 ${rampEndX - 40 * Math.cos(rad)},${baseY - 40 * Math.sin(rad)}`}
				fill="none"
				stroke="hsl(var(--muted-foreground))"
				strokeWidth={1.5}
			/>
			<text x={rampEndX - 55} y={baseY - 10} fontSize={12} fill="hsl(var(--muted-foreground))">{angle}&deg;</text>
			{/* Object on ramp */}
			<g transform={`translate(${objX}, ${objY}) rotate(${-angle})`}>
				<rect x={-30} y={-25} width={60} height={50} rx={4} fill="hsl(220, 70%, 55%)" stroke="hsl(220, 70%, 35%)" strokeWidth={2} />
			</g>
		</g>
	);
}

function ElevatorScene({ cx, cy, width, height, shape }: SceneProps) {
	const eW = width * 0.45;
	const eH = height * 0.7;
	const eX = cx - eW / 2;
	const eY = cy - eH / 2 + 10;

	return (
		<g>
			{/* Elevator shaft */}
			<rect x={eX} y={eY} width={eW} height={eH} rx={6} fill="hsl(var(--muted))" stroke="hsl(var(--muted-foreground))" strokeWidth={2} />
			{/* Elevator doors */}
			<line x1={cx} y1={eY + 10} x2={cx} y2={eY + eH - 10} stroke="hsl(var(--muted-foreground))" strokeWidth={1} strokeDasharray="4 4" opacity={0.5} />
			{/* Floor line */}
			<line x1={eX + 10} y1={eY + eH - 30} x2={eX + eW - 10} y2={eY + eH - 30} stroke="hsl(var(--muted-foreground))" strokeWidth={2} />
			{/* Cable */}
			<line x1={cx} y1={0} x2={cx} y2={eY} stroke="hsl(var(--muted-foreground))" strokeWidth={2} strokeDasharray="6 3" />
			{/* Arrow indicators */}
			{/* Person inside */}
			<ObjectShape cx={cx} cy={eY + eH - 65} shape={shape} size={30} />
		</g>
	);
}

function HangingScene({ cx, cy, width, height, shape }: SceneProps) {
	const anchorY = height * 0.12;
	const objY = cy + 10;

	return (
		<g>
			{/* Ceiling */}
			<line x1={width * 0.2} y1={anchorY} x2={width * 0.8} y2={anchorY} stroke="hsl(var(--muted-foreground))" strokeWidth={2} />
			{Array.from({ length: 8 }, (_, i) => (
				<line key={i} x1={width * 0.2 + i * ((width * 0.6) / 8)} y1={anchorY} x2={width * 0.2 + i * ((width * 0.6) / 8) - 8} y2={anchorY - 10} stroke="hsl(var(--muted-foreground))" strokeWidth={1} opacity={0.5} />
			))}
			{/* Rope */}
			<line x1={cx} y1={anchorY} x2={cx} y2={objY - 25} stroke="hsl(var(--muted-foreground))" strokeWidth={2} />
			{/* Object */}
			<ObjectShape cx={cx} cy={objY} shape={shape} />
		</g>
	);
}

function ProjectileScene({ cx, cy, width, height, shape }: SceneProps) {
	// Dotted path showing trajectory
	return (
		<g>
			{/* Faint motion trail */}
			{[0.15, 0.3, 0.45].map((f, i) => (
				<circle key={i} cx={cx} cy={cy - (3 - i) * 40} r={12} fill="hsl(220, 70%, 55%)" opacity={0.1 + i * 0.08} />
			))}
			{/* Downward motion arrow */}
			<line x1={cx + 45} y1={cy - 50} x2={cx + 45} y2={cy + 50} stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="5 3" opacity={0.4} />
			<polygon points={`${cx + 45},${cy + 55} ${cx + 40},${cy + 45} ${cx + 50},${cy + 45}`} fill="hsl(var(--muted-foreground))" opacity={0.4} />
			{/* Object */}
			<ObjectShape cx={cx} cy={cy} shape={shape} />
		</g>
	);
}
